import { useCallback, useEffect, useMemo, useState } from 'react';
import { reviews as publishedReviews, reviewsAggregate } from '../data/reviews';
import type { Review } from '../types/review';
import { summarize } from '../utils/reviews';

const STORAGE_KEY = 'mf-review-submitted';
const EVENT = 'mf-review-change';

function readMine(): Review | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Review) : null;
  } catch {
    return null;
  }
}

/**
 * Published reviews come only from data/reviews.ts (real reviews).
 * A visitor's own submission is kept on this device as "pending" — it is shown
 * to them but never counted in the public average until it is moderated
 * and published through the review platform.
 */
export function useReviews() {
  const [mine, setMine] = useState<Review | null>(null);

  useEffect(() => {
    setMine(readMine());
    const sync = () => setMine(readMine());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Owner's lifetime aggregate takes precedence while it exceeds the reviews loaded on site.
  const summary = useMemo(() => {
    const fromList = summarize(publishedReviews);
    if (reviewsAggregate && reviewsAggregate.count > fromList.count) {
      return { ...fromList, count: reviewsAggregate.count, average: reviewsAggregate.average, isAggregate: true };
    }
    return { ...fromList, isAggregate: false };
  }, []);

  const submit = useCallback((review: Omit<Review, 'id' | 'date' | 'status' | 'verified'>) => {
    const full: Review = {
      ...review,
      id: `local-${Date.now()}`,
      date: new Date().toISOString(),
      status: 'pending',
      verified: false,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(full));
    window.dispatchEvent(new Event(EVENT));
    // Production: POST to the review platform (Judge.me / Loox API) for moderation.
  }, []);

  return { published: publishedReviews, mine, hasReviewed: Boolean(mine), summary, submit };
}
